import type { XYPosition } from '@xyflow/react';
import type { Topology, TopologyEdge } from '@/services/observability-api';
import type {
  ArchitectureFlowEdge,
  ArchitectureFlowNode,
  GraphDetailLevel,
  MetricOverlay,
  ProtocolFilter,
} from './types';

const NODE_WIDTH = 224;
const NODE_HEIGHT = 112;
const elkPromise = import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK());

export function formatRequestRate(rate: number): string {
  return `${rate.toFixed(rate >= 10 ? 0 : 1)} req/s`;
}

export function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(rate > 0.01 ? 1 : 2)}%`;
}

export function detailLevelForZoom(zoom: number): GraphDetailLevel {
  if (zoom < 0.75) return 'overview';
  if (zoom >= 1.25) return 'detailed';
  return 'standard';
}

export function filterTopology(topology: Topology, filter: ProtocolFilter): Topology {
  return filterTopologyByNodes(topology, filter);
}

export function adjacentNodeIds(
  topology: Topology,
  filter: ProtocolFilter,
  nodeId: string
): string[] {
  const edges = protocolEdges(topology, filter);
  const adjacent = new Set<string>([nodeId]);
  for (const edge of edges) {
    if (edge.source === nodeId) adjacent.add(edge.target);
    if (edge.target === nodeId) adjacent.add(edge.source);
  }
  return [...adjacent];
}

export function filterTopologyByNodes(
  topology: Topology,
  filter: ProtocolFilter,
  visibleNodeIds?: ReadonlySet<string>
): Topology {
  const edges = protocolEdges(topology, filter);
  const connectedNodeIds = new Set(edges.flatMap((edge) => [edge.source, edge.target]));
  const nodes = topology.nodes.filter((node) =>
    visibleNodeIds ? visibleNodeIds.has(node.id) : filter === 'all' || connectedNodeIds.has(node.id)
  );
  const shownNodeIds = new Set(nodes.map((node) => node.id));
  return {
    ...topology,
    nodes,
    edges: edges.filter((edge) => shownNodeIds.has(edge.source) && shownNodeIds.has(edge.target)),
  };
}

function protocolEdges(topology: Topology, filter: ProtocolFilter) {
  const edges = topology.edges.filter((edge) => {
    if (filter === 'all') return true;
    if (filter === 'http') return edge.protocol === 'http' || edge.protocol === 'beacon';
    if (filter === 'live') return edge.protocol === 'websocket' || edge.protocol === 'webrtc';
    return edge.protocol === 'publish' || edge.protocol === 'consume';
  });
  return edges;
}

export function createFlowTopology(
  topology: Topology,
  filter: ProtocolFilter,
  overlay: MetricOverlay,
  detailLevel: GraphDetailLevel,
  visibleNodeIds?: ReadonlySet<string>
): { nodes: ArchitectureFlowNode[]; edges: ArchitectureFlowEdge[] } {
  const filtered = filterTopologyByNodes(topology, filter, visibleNodeIds);
  const eventNodeIds = new Set(
    topology.edges
      .filter((edge) => edge.protocol === 'publish' || edge.protocol === 'consume')
      .flatMap((edge) => [edge.source, edge.target])
  );
  const nodes = filtered.nodes.map<ArchitectureFlowNode>((topologyNode) => ({
    id: topologyNode.id,
    type: 'architecture',
    position: { x: 0, y: 0 },
    data: { topologyNode, detailLevel, hasEventTraffic: eventNodeIds.has(topologyNode.id) },
  }));
  const edges = filtered.edges.map<ArchitectureFlowEdge>((topologyEdge) => ({
    id: topologyEdge.id,
    type: 'architecture',
    source: topologyEdge.source,
    target: topologyEdge.target,
    data: { topologyEdge, overlay },
    animated: topologyEdge.protocol === 'publish' || topologyEdge.protocol === 'consume',
  }));
  return { nodes, edges };
}

export async function layoutFlowTopology(
  nodes: ArchitectureFlowNode[],
  edges: ArchitectureFlowEdge[],
  savedPositions: Record<string, XYPosition>
): Promise<ArchitectureFlowNode[]> {
  const elk = await elkPromise;
  const graph = await elk.layout({
    id: 'architecture-observability',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.layered.spacing.nodeNodeBetweenLayers': '96',
      'elk.spacing.nodeNode': '40',
    },
    children: nodes.map((node) => ({ id: node.id, width: NODE_WIDTH, height: NODE_HEIGHT })),
    edges: edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
  });

  const layoutPositions = Object.fromEntries(
    (graph.children ?? []).map((node) => [node.id, { x: node.x ?? 0, y: node.y ?? 0 }])
  );
  return nodes.map((node) => ({
    ...node,
    position: savedPositions[node.id] ?? layoutPositions[node.id] ?? { x: 0, y: 0 },
  }));
}

export function edgeClassName(data: NonNullable<ArchitectureFlowEdge['data']>): string {
  if (data.topologyEdge.evidence === 'configured')
    return 'stroke-1 stroke-neutral-400 architecture-configured-edge';
  const { errorRate, p95LatencyMs, protocol, requestRate } = data.topologyEdge;
  const width = requestRate >= 20 ? 'stroke-[3]' : requestRate >= 5 ? 'stroke-2' : 'stroke-1';
  const protocolClass =
    protocol === 'http' || protocol === 'beacon'
      ? 'stroke-lavender-600'
      : protocol === 'publish' || protocol === 'consume'
        ? 'architecture-event-edge stroke-peach-600'
        : 'stroke-cyan-600';
  if (data.overlay === 'errors' && errorRate > 0.02) return `${width} stroke-red-600`;
  if (data.overlay === 'latency' && p95LatencyMs > 700) return `${width} stroke-amber-600`;
  return `${width} ${protocolClass}`;
}

export function edgeMarkerColor(data: NonNullable<ArchitectureFlowEdge['data']>): string {
  if (data.topologyEdge.evidence === 'configured') return '#a3a3a3';
  const { errorRate, p95LatencyMs, protocol } = data.topologyEdge;

  if (data.overlay === 'errors' && errorRate > 0.02) return '#dc2626';
  if (data.overlay === 'latency' && p95LatencyMs > 700) return '#d97706';
  return protocol === 'http' || protocol === 'beacon'
    ? '#8257f5'
    : protocol === 'publish' || protocol === 'consume'
      ? '#f25533'
      : '#0891b2';
}

export function edgeActivityLabel(edge: TopologyEdge): string {
  if (edge.evidence === 'configured') return 'No observed traffic';
  const prefix = edge.trafficType === 'telemetry' ? 'Telemetry · ' : '';
  if (edge.protocol === 'beacon')
    return `${prefix}${(edge.activity?.queued ?? 0).toFixed(0)} queued`;
  if (edge.protocol === 'websocket' || edge.protocol === 'webrtc')
    return `${edge.protocol === 'webrtc' ? 'WebRTC' : 'WebSocket'} · ${(edge.activity?.attempts ?? 0).toFixed(0)} attempts`;
  return prefix + formatRequestRate(edge.requestRate);
}
